import { Switch, Menu } from '@headlessui/react'
import Translation from 'components/Translation'
import { useEffect, useState } from 'react'
import { addAlpha, classNames } from 'utils/dom-helpers'
import { RadioGroup } from '@headlessui/react'
import GenericSingleSelector from 'components/DropdownSelectors/GenericSingleSelector'
import { ChevronDownIcon } from '@heroicons/react/solid'
import { repeat_options } from 'components/Modals/Service/GroupClassRowForm'
import DateInput from 'components/DateInput'
import { RecurrenceSchedule } from 'components/Modals/types'
import { useAppContext } from 'context/AppContext'
import { getClosestDateFromDay, toHHMM, toHHMM12, toYmd } from 'utils/string-localization-helper'
import { useTenantContext } from 'context/TenantContext'
import { ServiceApiTypeEnum, ServiceItem } from 'types/service'
import { AvailabilityDateTimeDTO, BookingConfirmPaymentDTO, BookingConfirmPaymentWithoutBookingDTO, BookingDTO, OrderBookingDTO, OrderBookingsDTO, OrderBookingWithoutBookingDTO, RecurringBookingDates } from 'types/order-booking'
import { getApiRequest } from 'utils/fetch-helper'
import CustomDateCell from './CustomDateCell'
import { ClientMembershipItem } from 'types/client-membership'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import { IntakeClientResponse, mapClientResponseToDTO } from 'types/intake-forms'

enum ScheduleType {
    RECURRING = "booking.recurring",
    CUSTOM_DATES = "booking.custom_dates"
}

const RECURRING_BOOKINGS_LIMIT = 20

const days_of_week = [ //TODO: should refactor all the other arrays to use lowercase in the first place
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
]

//TODO: refactor, should ask BE to accept 1 DTO and an array of dates instead
export function generateRecurringOrderBookingsDTO(
    dto: OrderBookingWithoutBookingDTO, 
    booking: BookingDTO, 
    dates: RecurringBookingDates[], 
    duration: number, 
    client_responses?: IntakeClientResponse[],
    remove_staff_availability?: boolean,
    staff_id?: string
): OrderBookingsDTO {
    let {
        amount,
        order
    } = dto

    let bookings = dates.filter((date) => !date.error_tag).map((date) => {
        let booking_copy = structuredClone(booking)
        if (booking.groupClassAvailabilityId) {
            booking_copy.groupClassAvailabilityId = date.group_class_availability_id
        } else if (booking_copy.appointmentDto) {
            let end_date = new Date(date.date.getTime())
            end_date.setMinutes(end_date.getMinutes() + duration)
            booking_copy.appointmentDto = {
                ...booking_copy.appointmentDto,
                staffAvailabilityId: date.staff_availability_id,
                staffId: date.staff_id,
                date: toYmd(date.date),
                endTime: toHHMM12(end_date),
                startTime: toHHMM12(date.date),
            }
            if (remove_staff_availability) {
                booking_copy.appointmentDto = {
                    ...booking_copy.appointmentDto,
                    staffAvailabilityId: undefined,
                    staffId: staff_id
                }
            }
        }
        return booking_copy
    })
    return {
        amount,
        bookings,
        order,
        responses: client_responses?.map(mapClientResponseToDTO)
    } as OrderBookingsDTO
}

export function generateRecurringBookingConfirmPaymentDTO(dto: BookingConfirmPaymentWithoutBookingDTO, booking: BookingDTO, dates: RecurringBookingDates[], duration: number, client_responses?: IntakeClientResponse[]): BookingConfirmPaymentDTO {
    let {
        amount,
        order,
        savePaymentMethod,
        tenantId,
        paymentIntentId,
        paymentMethodId
    } = dto

    let bookings: BookingDTO[] = dates.filter((date) => !date.error_tag).map((date) => {
        let booking_copy: BookingDTO = structuredClone(booking)
        if (booking.groupClassAvailabilityId) {
            booking_copy.groupClassAvailabilityId = date.group_class_availability_id
        } else if (booking_copy.appointmentDto) {
            let end_date = new Date(date.date.getTime())
            end_date.setMinutes(end_date.getMinutes() + duration)
            booking_copy.appointmentDto.staffAvailabilityId = date.staff_availability_id
            booking_copy.appointmentDto = {
                ...booking_copy.appointmentDto,
                staffAvailabilityId: date.staff_availability_id,
                date: toYmd(date.date),
                endTime: toHHMM12(end_date),
                startTime: toHHMM12(date.date),
            }
        }
        return booking_copy
    })
    return {
        amount,
        bookings,
        order,
        tenantId,
        savePaymentMethod,
        paymentIntentId,
        paymentMethodId,
        responses: client_responses?.map(mapClientResponseToDTO)
    } as BookingConfirmPaymentDTO
}

interface RecurringBookingsProps {
    primary_color: string
    start: Date
    end: Date
    is_recurring: boolean
    toggleRecurring: (toggle: boolean) => void
    recurring_dates: RecurringBookingDates[]
    setRecurringDates: (dates: RecurringBookingDates[]) => void
    service_id: string
    service_type: ServiceApiTypeEnum
    location_id: string
    is_editing: boolean
    toggleEditing: (editing: boolean) => void
    grid_count: number
    valid_recurring_count: number
    setValidRecurringCount: (count: number) => void
    membership_client?: ClientMembershipItem
}

export default function RecurringBookings(props: RecurringBookingsProps) {
    const {
        primary_color,
        start,
        end,
        is_recurring,
        toggleRecurring,
        recurring_dates,
        setRecurringDates,
        service_id,
        service_type,
        location_id,
        is_editing,
        toggleEditing,
        grid_count,
        valid_recurring_count,
        setValidRecurringCount,
        membership_client
    } = props //TOOD: maybe make a recurring bookings context?
    const {
        locale,
        setGlobalLoading
    } = useAppContext()
    const {
        services
    } = useTenantContext()
    const { setGlobalError } = useAppErrorApiContext()
    const [schedule_type, setScheduleType] = useState<ScheduleType>(ScheduleType.RECURRING)
    const [day, setDay] = useState<number>(start.getDay())
    const [repeats, setRepeats] = useState<RecurrenceSchedule>(RecurrenceSchedule.WEEKLY)
    const [end_date, setEndDate] = useState<Date>(end)
    const [custom_dates, setCustomDates] = useState<Date[]>([])
    const [service, setService] = useState<ServiceItem | undefined>()
    const [timer, setTimer] = useState<any>()
    const [is_date_picker_open, toggleDatePickerOpen] = useState<boolean>(false)

    useEffect(() => {
        if (schedule_type === ScheduleType.RECURRING) {
            try {
                setGlobalLoading(true)
                let dates: Date[] = []
                var date = new Date(getClosestDateFromDay(day, start).getTime())
                let increment
                switch (repeats) {
                    case RecurrenceSchedule.MONTHLY_ON_DAY:
                        increment = () => {
                            const day_of_week = date.getDay()
                            const num_of_week = Math.ceil(date.getDate() / 7)
                            let new_date = new Date(date.getFullYear(), date.getMonth() + 1)
                            new_date.setDate(1 + (7 - new_date.getDay() + day_of_week) % 7 + (num_of_week - 1) * 7)
                            new_date.setHours(date.getHours())
                            new_date.setMinutes(date.getMinutes())
                            date = new_date
                        }
                        break
                    case RecurrenceSchedule.MONTHLY_ON_DATE:
                        increment = () => {
                            date.setMonth(date.getMonth() + 1)
                        }
                        break
                    case RecurrenceSchedule.WEEKLY:
                        increment = () => {
                            date.setDate(date.getDate() + 7)
                        }
                        break
                    default: //Default is DAILY
                        increment = () => {
                            date.setDate(date.getDate() + 1)
                        }
                        break
                }

                do {
                    dates.push(
                        new Date(date)
                    )
                    increment()
                } while (date.getTime() <= end_date.getTime())
                getDateAvails(dates)
            } catch (error) {
                setGlobalError(error)
            } finally {
                setGlobalLoading(false)
            }
        }

    }, [day, repeats, schedule_type, end_date, membership_client])

    function getDatesWithError(dates: RecurringBookingDates[], left: number, increment: number): RecurringBookingDates[] {
        let last_index = dates.length - 1
        let left_copy = left
        do {
            if (!dates[last_index].error_tag) {
                dates[last_index].error_tag = "booking.not_entitle_to_date"
                left_copy = left_copy + increment
            }
            last_index--
        } while (last_index > -1 && left_copy < 0)
        return dates
    }

    async function getDateAvails(dates: Date[]) {
        const date_string = dates.map((c) => `${toYmd(c)}T${toHHMM(c)}.000`).join(",")
        let avails: AvailabilityDateTimeDTO[] = service_type == ServiceApiTypeEnum.APPOINTMENT
            ? await getApiRequest(`/v1/offerings/check-availability/service-with-dates?dateTimes=${date_string}&locationId=${location_id}&serviceId=${service_id}`)
            : await getApiRequest(`/v1/offerings/group-class-with-dates?dateTimes=${date_string}&locationId=${location_id}&serviceId=${service_id}`)
        let valid_count = 0
        let limit_reached = false

        if (avails?.length > 0) {
            avails.sort((a, b) => a.dateTime.localeCompare(b.dateTime))
        }
        
        let copy = dates.map((date, index) => {
            let is_valid = (avails?.[index]?.staffAvailabilities?.[0]?.staffAvailabilityId || avails?.[index]?.groupClassAvailabilities?.[0]?.id)
            if (is_valid && !limit_reached) {
                if (valid_count < RECURRING_BOOKINGS_LIMIT) {
                    valid_count++
                } else {
                    limit_reached = true
                }
            }
            return {
                date,
                staff_id: avails?.[index]?.staffAvailabilities?.[0]?.staffId,   // for appointments only
                staff_availability_id: avails?.[index]?.staffAvailabilities?.[0]?.staffAvailabilityId,
                group_class_availability_id: avails?.[index]?.groupClassAvailabilities?.[0]?.id,
                error_tag: !limit_reached ? (is_valid ? undefined : "booking.no_availability_for_date_time") : "booking.max_number_recurring_reached" //TODO: refactor this
            } as RecurringBookingDates
        })
        if (valid_count != 0 && membership_client?.id) {
            let usage = await getApiRequest(`/v1/memberships/clients/get-usage?bookingsCount=${valid_count}&membershipClientId=${membership_client.id}&serviceId=${service?.id}`)
            if (usage) {
                if (!usage.unlimited) { //TODO: optimize
                    if (usage.amountLeft < 0) {
                        copy = getDatesWithError(copy, usage.amountLeft, membership_client.service_price)
                    } else if (usage.minutesLeft < 0) {
                        copy = getDatesWithError(copy, usage.minutesLeft, service?.duration || 1)
                    } else if (usage.serviceLeft < 0) {
                        copy = getDatesWithError(copy, usage.serviceLeft, 1)
                    }
                    valid_count = copy.filter((c) => !c.error_tag).length
                }
            } else {
                throw usage
            }
        }
        setRecurringDates(copy)
        setValidRecurringCount(valid_count)
    }

    useEffect(() => { //TODO: refactor to track what dates were updated and refetch only those
        if (schedule_type == ScheduleType.CUSTOM_DATES && !is_editing) {
            try {
                setGlobalLoading(true)
                let sorted = custom_dates.slice()
                sorted.sort((a, b) => a.getTime() - b.getTime())
                getDateAvails(sorted)
            } catch (error) {
                setGlobalError(error)
            } finally {
                setGlobalLoading(false)
            }
        }
    }, [custom_dates, is_editing, schedule_type, membership_client])

    useEffect(() => {
        if (service_id && services && services.length > 0) {
            let service = services.find((service) => service.id == service_id)
            setService(service)
        } else {
            setService(undefined)
        }
    }, [service_id, services])

    function handleDebounce() {
        if (!is_editing) {
            toggleEditing(true)
        }
        if (timer) {
            clearTimeout(timer)
            setTimer(undefined)
        }
        setTimer(
            setTimeout(async () => {
                toggleEditing(false)
            }, 1200)
        )
    }

    return (
        <>
            {service &&
                <div className='flex flex-col'>
                    <div className='flex flex-row gap-3 items-center text-sm'>
                        <Switch
                            onChange={(is_recurring: boolean) => {
                                toggleRecurring(is_recurring)
                            }}
                            checked={is_recurring}
                            className={'relative inline-flex flex-shrink-0 h-[24px] w-[48px] border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75'}
                            style={{
                                backgroundColor: addAlpha(is_recurring ? primary_color : '#23476B', is_recurring ? 1 : 0.3)
                            }}
                        >
                            <span
                                aria-hidden="true"
                                className={`${is_recurring ? 'translate-x-6' : 'translate-x-0'
                                    }
                pointer-events-none inline-block h-[20px] w-[20px] rounded-full bg-white shadow-lg transform ring-0 transition ease-in-out duration-200`}
                            />
                        </Switch>
                        <Translation content_key='booking.book_additional_dates' />
                    </div>
                    {is_recurring &&
                        <div className='flex flex-col rounded-lg bg-neutral-50 mt-5 p-4'>
                            <RadioGroup
                                value={schedule_type}
                                onChange={(v: ScheduleType) => {
                                    setScheduleType(v)
                                }}
                            >
                                <RadioGroup.Label
                                    className={classNames('text-xs block font-[450]')}
                                >
                                    <Translation content_key="booking.select_schedule_type" />
                                </RadioGroup.Label>
                                <div className="flex gap-4 mt-4">
                                    {Object.values(ScheduleType).map((v, idx) => {
                                        return (
                                            <RadioGroup.Option
                                                key={v}
                                                value={v}
                                            >
                                                {({ checked }) => (
                                                    <div className="flex items-center gap-2 text-sm cursor-pointer">
                                                        <div
                                                            className={classNames(
                                                                checked
                                                                    ? 'border-2'
                                                                    : 'border bg-white',
                                                                'rounded-full w-6 h-6 flex items-center'
                                                            )}
                                                            style={{
                                                                borderColor: checked ? primary_color : '#DDE2E5'
                                                            }}
                                                        >
                                                            <i
                                                                className={classNames(
                                                                    checked
                                                                        ? primary_color
                                                                        : '',
                                                                    'rounded-full w-4 h-4 block m-auto'
                                                                )}
                                                                style={{
                                                                    backgroundColor: checked ? primary_color : '#ffffff'
                                                                }}
                                                            />
                                                        </div>
                                                        <span
                                                            className={
                                                                checked
                                                                    ? 'text-primary-light'
                                                                    : ''
                                                            }
                                                        >
                                                            <Translation
                                                                content_key={v}
                                                            />
                                                        </span>
                                                    </div>
                                                )}
                                            </RadioGroup.Option>
                                        )
                                    })}
                                </div>
                            </RadioGroup>
                            <div className='w-full  my-3' />
                            {schedule_type == ScheduleType.RECURRING
                                ?
                                <div className='flex flex-row items-center flex-wrap gap-4'>
                                    <div className=' items-center w-[45%]'>
                                        <Translation
                                            render_as='label'
                                            content_key='booking.day'
                                            className='aot-form-label'
                                        />
                                        <GenericSingleSelector
                                            class_name='w-full'
                                            items={days_of_week}
                                            selected_item={day}
                                            isItemSelected={(item, selected_item) => {
                                                return item === days_of_week[selected_item]
                                            }}
                                            titleDisplay={(item, is_open) => {
                                                return (
                                                    <>
                                                        <Translation
                                                            content_key={days_of_week[item]}
                                                            render_as='span'
                                                            className='flex flex-grow capitalize'
                                                        />
                                                        <ChevronDownIcon
                                                            className="-mr-1 ml-2 h-5 w-5 text-primary-light"
                                                            aria-hidden="true"
                                                        />
                                                    </>
                                                )
                                            }}
                                            itemDisplay={(item: string, index: number, is_selected: boolean) => {
                                                return (
                                                    <Menu.Item key={item}>
                                                        {({ active }) => (
                                                            <div
                                                                className={classNames(
                                                                    active
                                                                        ? 'bg-[#86C5E0]/10'
                                                                        : 'bg-white',
                                                                    'flex flex-col text-xs font-medium font-[#58595B] p-2 rounded-lg whitespace-normal',
                                                                )}
                                                                onClick={() => {
                                                                    setDay(index)
                                                                }}
                                                            >
                                                                <Translation
                                                                    content_key={item}
                                                                    render_as='span'
                                                                    className='text-sm font-medium text-gray-700 capitalize'
                                                                />
                                                            </div>
                                                        )}
                                                    </Menu.Item>
                                                )
                                            }}
                                        />
                                    </div>
                                    <div className=' items-center w-[45%]'>
                                        <Translation
                                            render_as='label'
                                            content_key='booking.repeats'
                                            className='aot-form-label'
                                        />
                                        <GenericSingleSelector
                                            class_name='w-full'
                                            items={repeat_options}
                                            selected_item={repeats}
                                            isItemSelected={(item, selected_item) => {
                                                return item === selected_item
                                            }}
                                            titleDisplay={(item, is_open) => {
                                                return (
                                                    <>
                                                        <Translation
                                                            content_key={`booking.repeats_${item.toLowerCase()}`}
                                                            render_as='span'
                                                            className='flex flex-grow capitalize'
                                                        />
                                                        <ChevronDownIcon
                                                            className="-mr-1 ml-2 h-5 w-5 text-primary-light"
                                                            aria-hidden="true"
                                                        />
                                                    </>
                                                )
                                            }}
                                            itemDisplay={(item: RecurrenceSchedule, index: number, is_selected: boolean) => {
                                                return (
                                                    <Menu.Item key={item}>
                                                        {({ active }) => (
                                                            <div
                                                                className={classNames(
                                                                    active
                                                                        ? 'bg-[#86C5E0]/10'
                                                                        : 'bg-white',
                                                                    'flex flex-col text-xs font-medium font-[#58595B] p-2 rounded-lg whitespace-normal',
                                                                )}
                                                                onClick={() => {
                                                                    setRepeats(item)
                                                                }}
                                                            >
                                                                <Translation
                                                                    content_key={`booking.repeats_${item.toLowerCase()}`}
                                                                    render_as='span'
                                                                    className='text-sm font-medium text-gray-700 capitalize'
                                                                />
                                                            </div>
                                                        )}
                                                    </Menu.Item>
                                                )
                                            }}
                                        />
                                    </div>
                                    <div className='booking-date items-center w-[45%] relative' onClick={() => toggleDatePickerOpen(true)}>
                                        <Translation content_key='booking.end_date' render_as='label' className='aot-form-label' />
                                        <DateInput
                                            id={`ends_date`}
                                            minDate={start}
                                            className={classNames(
                                                'border-0 rounded text-sm focus:ring-0 inline-block flex border border-gray-200 ',
                                            )}
                                            onChange={(date: Date) => {
                                                setEndDate(new Date(date))
                                            }}
                                            dateFormat="dd/MM/yyyy"
                                            selected={end_date}
                                            popperClassName="z-20"
                                            open={is_date_picker_open}
                                            onClickOutside={() => toggleDatePickerOpen(false)}
                                        />
                                        <i className='absolute feather-calendar text-xl right-2 top-7' />
                                    </div>
                                    {recurring_dates && recurring_dates.length > 0 &&
                                        <>
                                            <div className='w-full  my-3' />
                                            <div className='flex flex-col w-full text-xs'>
                                                {recurring_dates.map((recurring_date) => {
                                                    return (
                                                        <div
                                                            className={classNames('flex gap-4',
                                                                (!recurring_date.group_class_availability_id && !recurring_date.staff_availability_id)
                                                                    || recurring_date.error_tag
                                                                    ? "line-through opacity-50" : ""
                                                            )}
                                                            key={recurring_date.date.getTime()}
                                                        >
                                                            <div className='font-medium'>{
                                                                recurring_date.date.toLocaleString(locale, {
                                                                    weekday: 'long',
                                                                    day: 'numeric',
                                                                    month: 'short',
                                                                    year: 'numeric',
                                                                })
                                                            }
                                                            </div>
                                                            <div className='font-[450]'>
                                                                {toHHMM12(recurring_date.date, locale, true, true)}-{toHHMM12(end, locale, true, true)}
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </>
                                    }
                                </div>
                                :
                                <>
                                    <div className={`grid grid-cols-[${grid_count}] gap-x-4 gap-y-2`}>
                                        {custom_dates && custom_dates.length > 0 && custom_dates.map((custom_date, index) => {
                                            let base_date = new Date(custom_date.getTime())
                                            base_date.setHours(0)
                                            base_date.setMinutes(0)
                                            base_date.setSeconds(0)
                                            base_date.setMilliseconds(0)
                                            return (
                                                <CustomDateCell
                                                    key={index}
                                                    index={index}
                                                    start={start}
                                                    custom_date={custom_date}
                                                    base_date={base_date}
                                                    setCustomDate={(date: Date) => {
                                                        let copy = custom_dates.slice()
                                                        copy[index] = new Date(date.getTime())
                                                        setCustomDates(copy)
                                                    }}
                                                    handleDebounce={handleDebounce}
                                                    removeCustomDate={() => {
                                                        let copy = custom_dates.slice()
                                                        copy.splice(index, 1)
                                                        setCustomDates(copy)
                                                    }}
                                                />
                                            )
                                        })}

                                    </div>
                                    <button className='text-[#71717A] w-full items-center border border-dashed border-[#BCBEC0] p-2 rounded-lg text-sm flex justify-center mt-6 disabled:cursor-not-allowed disabled:opacity-50'
                                        onClick={() => {
                                            handleDebounce()
                                            let copy = custom_dates.slice()
                                            copy.push(start)
                                            copy.sort((a, b) => {
                                                return a.getTime() - b.getTime()
                                            })
                                            setCustomDates(copy)
                                        }}
                                        disabled={custom_dates.length >= RECURRING_BOOKINGS_LIMIT}
                                    >
                                        <i className="feather-plus mr-3 text-xl" />
                                        <Translation content_key='booking.add_another_schedule' />
                                    </button>
                                </>
                            }
                            {/* TODO: find another implementation that doesn't loop twice */}
                            {valid_recurring_count != recurring_dates.length &&
                                recurring_dates.filter((recurring) => recurring.error_tag).map((recurring) => {
                                    return (
                                        <div className='flex flex-row w-full items-center text-xs p-3 bg-yellow-500/[.05] mt-2' key={recurring.date.getTime()}>
                                            <i className="feather-alert-triangle text-sm text-yellow-500" />
                                            <span className='font-bold ml-3 whitespace-nowrap w-1/2'>{recurring.date.toLocaleString(locale, {
                                                weekday: 'long',
                                                day: 'numeric',
                                                month: 'short',
                                                year: 'numeric',
                                            })}
                                            </span>
                                            &nbsp;
                                            <Translation
                                                content_key={recurring.error_tag || ""}
                                                render_as='span'
                                                className='w-1/2'
                                            />
                                        </div>
                                    )
                                })
                            }
                        </div>
                    }
                </div>
            }
        </>

    )
}